import React, { useMemo, useState } from 'react';
import { RecordIdLabel } from './ui/idColumn';
import { formatDate } from '../utils/formatDate';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AccountBar from './account/AccountBar';
import AccountMetrics from './account/AccountMetrics';
import { projectMetrics } from './account/accountMetricSets';
import { useExport } from './header/exporters';
import BreadcrumbBar, { type BreadcrumbAction } from './BreadcrumbBar';
import { useRole } from '../hooks/useRole';
import Icon from './Icon';
import { roleLevel } from '../utils/roles';
import { editProjectPath, projectBillingPath } from '../utils/accountRoutes';
import PrimaryCurrencyField from './settings/PrimaryCurrencyField';
import { useNotifications } from './NotificationProvider';
import { errorMessageOf } from '../utils/apiErrors';
import { getActiveProjectId } from '../utils/session';
import { matchesWorkspace } from '../utils/accountSearch';
import { useHeaderSearch } from '../hooks/useHeaderSearch';
import EmptyState from './EmptyState';
import { usePageRefresh } from '../hooks/usePageRefresh';
import './ui/ui.css';
import type { ProjectCurrency } from '../utils/currency';
import '../styles/components/ProjectsView.css';

const formatProjectDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return formatDate(d);
};

const ProjectsView: React.FC = () => {
  const navigate = useNavigate();
  const { workspaces, switchWorkspace, createWorkspace, refreshWorkspaces } = useAuth();
  const { canEdit, canViewFinancials } = useRole();
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCurrency, setNewCurrency] = useState<ProjectCurrency>('');
  const { showError } = useNotifications();
  const [creating, setCreating] = useState(false);

  const activeProjectId = getActiveProjectId();
  // The header search narrows the cards.
  const search = useHeaderSearch();
  const shown = useMemo(() => workspaces.filter(ws => matchesWorkspace(ws, search)), [workspaces, search]);

  // Export button: the projects listed right now; contract value only for a role that may see money.
  useExport('account-projects', { rows: shown, ctx: { showMoney: canViewFinancials } });

  // The header's Refresh reads the project list again.
  usePageRefresh(() => refreshWorkspaces());

  const handleOpen = async (projectId: string) => {
    if (switchingId) return;
    // Already the active project — just go straight into it instead of no-op-ing.
    if (projectId === activeProjectId) {
      navigate('/dashboard');
      return;
    }
    setSwitchingId(projectId);
    try {
      await switchWorkspace(projectId);
    } catch (err) {
      showError(errorMessageOf(err, "Couldn't open that project. Please try again."));
      setSwitchingId(null);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newCurrency || creating) return;
    setCreating(true);
    try {
      await createWorkspace(newName.trim(), newCurrency);
    } catch (err) {
      showError(errorMessageOf(err, "Couldn't create the project. Please try again."));
      setCreating(false);
    }
  };

  const closeCreate = () => { setShowCreate(false); setNewName(''); setNewCurrency(''); };

  const barActions: BreadcrumbAction[] = canEdit
    ? [{ kind: 'primary', key: 'new', icon: 'plus', label: 'New project', onClick: () => setShowCreate(true) }]
    : [];

  return (
    <div className="wt-container fade-in">
        <AccountBar
          view="projects"
          actions={<BreadcrumbBar actions={barActions} />}
          exportKey="account-projects"
        />
        {workspaces.length > 0 && (
          <AccountMetrics label="Projects summary" title="These figures follow the search box at the top" metrics={projectMetrics(shown)} />
        )}

        {workspaces.length === 0 && (
          <EmptyState
            compact
            icon="folder"
            title="No projects yet"
            description={canEdit ? 'Create the first one with New project in the bar above.' : 'Projects you are added to will show up here.'}
          />
        )}
        {shown.length === 0 && workspaces.length > 0 && (
          <EmptyState compact icon="folder" title="No projects match your search." />
        )}
        <div className="pv-grid">
          {shown.map(ws => {
            const dateLabel = formatProjectDate(ws.project_date);
            const isActive = ws.project_id === activeProjectId;
            // Edit project and Billing belong to the project's card; planner level and above, on that project.
            const mayEdit = canEdit && roleLevel(ws.role) >= roleLevel('planner');
            return (
              <article
                key={ws.project_id}
                className={`pv-card${isActive ? ' pv-card--active' : ''}${switchingId !== null ? ' pv-card--busy' : ''}`}
              >
              <button
                type="button"
                className="pv-card__open"
                onClick={() => handleOpen(ws.project_id)}
                disabled={switchingId !== null}
              >
                <RecordIdLabel kind="project" id={ws.project_id} />
                {dateLabel && <div className="pv-card-date">{dateLabel}</div>}
                <h3 className="pv-card-name">{ws.project_name}</h3>
                <span className="pv-card-role">{ws.role}</span>
                <div className="pv-card-readiness">
                  <div className="pv-card-readiness-label">
                    <span>Readiness</span>
                    <span>{ws.readiness}%</span>
                  </div>
                  <div className="pv-card-readiness-bar">
                    <div className="pv-card-readiness-fill" style={{ /* ds-dynamic: readiness width from data */ width: `${ws.readiness}%` }} />
                  </div>
                </div>
                {isActive && <span className="pv-card-badge">Current</span>}
                {switchingId === ws.project_id && <span className="pv-card-badge">Switching…</span>}
              </button>
              {mayEdit && (
                <div className="pv-card-actions">
                  <Link
                    className="header-action-btn"
                    to={editProjectPath(ws.project_id)}
                    aria-label={`Edit project ${ws.project_name}`}
                  >
                    <Icon name="edit" size={16} />
                    Edit project
                  </Link>
                  <Link
                    className="header-action-btn"
                    to={projectBillingPath(ws.project_id)}
                    aria-label={`Billing for ${ws.project_name}`}
                  >
                    <Icon name="creditCard" size={16} />
                    Billing
                  </Link>
                </div>
              )}
              </article>
            );
          })}
        </div>

      {showCreate && (
        <div className="pv-modal-backdrop" onClick={closeCreate}>
          <div className="pv-modal" onClick={e => e.stopPropagation()}>
            <h3 className="pv-modal-title">New project</h3>
            <p className="pv-modal-note">Name this project after the couple or event you're planning.</p>
            <input
              type="text"
              className="pv-modal-input"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="e.g. Alex & Sam's Wedding"
              autoFocus
            />
            <PrimaryCurrencyField value={newCurrency} locked={false} onChange={setNewCurrency} />
            <div className="pv-modal-actions">
              <button type="button" className="primary-btn" onClick={handleCreate} disabled={!newName.trim() || !newCurrency || creating}>
                {creating ? 'Creating…' : 'Create'}
              </button>
              <button type="button" className="btn-cancel" onClick={closeCreate}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsView;
